import React, {useCallback, useEffect, useMemo, useReducer, useRef} from 'react';
import {List} from 'immutable';
import type {Map as ImmutableMap} from 'immutable';
import type {Moment} from 'moment';
import notifier from '@albert-io/notifier';
import {addToast} from '@albert-io/atomic';

import constants from 'client/constants';
import errorCodes from 'client/errorCodes';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {genericMandarkRequest, invalidatePartialInterest} from 'resources/mandark.resource';
import type {CollectionType} from 'resources/mandark.resource';
import {query, resource} from '@albert-io/json-api-framework/request/builder';

import type {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';

import GoogleContext from '../Google.context';

/**
 * The error shapes gapi hands back. Every field is optional because which of them is
 * populated depends on which gapi surface failed — see `handleGoogleError`.
 */
interface GoogleError {
  error?: string;
  details?: string;
  message?: string;
  status?: number;
  body?: string;
  result?: {error?: {message?: string; code?: number}};
  response?: {body?: {errors?: Array<{code?: string}>}};
}

interface GoogleAuthUser {
  getAuthResponse: (includeAuthorizationData?: boolean) => {access_token: string};
  getBasicProfile: () => {getEmail: () => string};
}

interface GoogleAuthInstance {
  isSignedIn: {get: () => boolean};
  currentUser: {get: () => GoogleAuthUser};
  signIn: (options: {ux_mode: string}) => Promise<GoogleAuthUser>;
  signOut: () => Promise<void>;
  disconnect: () => void;
}

interface Gapi {
  load: (
    libraries: string,
    config: {
      callback: () => void;
      onerror: (error?: unknown) => void;
      timeout: number;
      ontimeout: () => void;
    }
  ) => void;
  client: {
    init: (config: {clientId: string; discoveryDocs: string[]; scope: string}) => Promise<void>;
    classroom: {
      courses: {courseWork: {create: (payload: Record<string, unknown>) => Promise<unknown>}};
    };
  };
  auth2: {getAuthInstance: () => GoogleAuthInstance | undefined};
}

declare global {
  // Injected by Google's externally-loaded script tag, so it is absent until that lands.
  var gapi: Gapi | undefined;
}

export interface ExportAssignmentArgs {
  assignment: AssignmentModelV3;
  assignmentLink: string;
  googleCourseId: string;
  successCallback?: () => void;
  errorCallback?: (error: unknown) => void;
  classroomDueDate?: Moment;
}

/** A roster student whose Google account could not be linked during a sync. */
export interface UnlinkedGoogleStudent {
  id: string;
  email: string;
}

export interface ImportClassroomArgs {
  callback?: (unlinkedStudents: UnlinkedGoogleStudent[]) => void;
  classroomId: string;
  googleClassroomId: string;
}

interface GoogleProviderState {
  classrooms: CollectionType;
  error: string | null;
  isRequestPending: boolean;
  isSignedIn: boolean;
  loggedInUserEmail: string | null;
}

export interface GoogleContextValue extends GoogleProviderState {
  clearError: () => void;
  handleExportAssignment: (args: ExportAssignmentArgs) => void;
  handleImportClassroom: (args: ImportClassroomArgs) => Promise<void>;
  handleSignIn: () => Promise<void>;
  handleSignOut: () => void;
}

interface Props {
  children?: React.ReactNode;
}

/**
 * Error handling for Google authentication using proper Honeybadger conventions
 */
const handleGoogleError = (error: unknown, context = '') => {
  const googleError = error as GoogleError | null | undefined;

  // Don't show notifications for user-initiated actions
  if (googleError?.error === 'popup_closed_by_user' || googleError?.error === 'access_denied') {
    return;
  }

  // Show user-friendly error message
  addToast({
    title: 'Google Error',
    color: 'negative',
    message: 'There was a problem with Google services. Please try again.'
  });

  // Build a proper Error for Honeybadger so gapi error details are captured.
  // gapi errors come in several shapes:
  //   - auth errors: {error: "idpiframe_initialization_failed", details: "Cookies are not enabled"}
  //   - API response errors: {result: {error: {message: "Forbidden", code: 403}}, status: 403, body: "..."}
  //   - standard Errors with .message/.stack
  //   - occasionally empty objects {}
  const nestedApiMessage = googleError?.result?.error?.message;
  const nestedApiCode = googleError?.result?.error?.code || googleError?.status;
  const errorMessage =
    googleError?.message ||
    (nestedApiMessage && nestedApiCode
      ? `Google API ${nestedApiCode}: ${nestedApiMessage}`
      : null) ||
    nestedApiMessage ||
    (googleError?.error && googleError?.details
      ? `${googleError.error}: ${googleError.details}`
      : null) ||
    googleError?.error ||
    googleError?.details ||
    'Unknown Google error';
  const normalizedError = error instanceof Error ? error : new Error(errorMessage);

  // Serialize the original error for context, guarding against empty objects
  let serializedOriginal: string | null = null;
  if (!(error instanceof Error)) {
    try {
      const json = JSON.stringify(error);
      serializedOriginal = json === '{}' ? `Empty object (type: ${typeof error})` : json;
    } catch {
      serializedOriginal = `Unserializable (type: ${typeof error})`;
    }
  }

  notifier.notify(normalizedError, {
    message: `Google ${context} failed`,
    tags: 'GoogleProvider',
    context: {
      gapiError: googleError?.error || null,
      gapiDetails: googleError?.details || null,
      gapiStatus: nestedApiCode || null,
      gapiResult: googleError?.result ? JSON.stringify(googleError.result) : null,
      gapiBody: googleError?.body || null,
      originalError: serializedOriginal
    }
  });
};

const scopes = `\
https://www.googleapis.com/auth/classroom.courses.readonly \
https://www.googleapis.com/auth/classroom.rosters.readonly \
https://www.googleapis.com/auth/classroom.profile.emails \
https://www.googleapis.com/auth/classroom.coursework.students \
profile`;

// Writing the token to state somewhere is probably irresponsible. We deliberately keep it in memory here instead.
let googleUserToken: string | null = null;

/**
 * Albert API layer
 */
const getClassroomsQuery = () =>
  resource('google_classroom_api_courses_v1')
    .mandarkEndpoint(['google_classroom_api', 'courses'])
    .customHeader({'google-classroom-api-token': googleUserToken});

const initialState: GoogleProviderState = {
  classrooms: List(),
  error: null,
  isRequestPending: false,
  isSignedIn: false,
  loggedInUserEmail: null
};

/**
 * Merge semantics are `this.setState`'s: the state keys move together (a sign-in settles
 * `isSignedIn`, `isRequestPending` and `loggedInUserEmail` at once), and consumers see one
 * new context value per update rather than one per key.
 */
const mergeState = (state: GoogleProviderState, patch: Partial<GoogleProviderState>) => ({
  ...state,
  ...patch
});

/**
 * Google Context Provider
 * =======================
 *
 * This componenet wraps App.react.js and makes Google implementation available via context.
 *
 * The component's handlers interface between Google's API and a state object.
 * We pass this state object to the context provider's value prop, where our context consumers
 * can then interact with it.  This can eventually replace GoogleClassroom.store.js.
 *
 * Deliberately un-annotated for the React Compiler: it renders `{children}` — the whole app —
 * so a cached subtree here would cut the legacy `forceUpdate` cascade off from every
 * unbridged descendant beneath it.
 */
export default function GoogleProvider({children}: Props) {
  const [state, setState] = useReducer(mergeState, initialState);
  const {isSignedIn} = state;

  const wasSignedInRef = useRef(isSignedIn);
  const shouldInvalidateClassroomsRef = useRef(false);

  const clearError = useCallback(() => setState({error: null}), []);

  const handleGoogleAuth = useCallback(async () => {
    const googleApi = global.gapi!;

    try {
      await googleApi.client.init({
        clientId: constants.GCLOUD_CLIENT_ID,
        discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/classroom/v1/rest'],
        scope: scopes
      });

      const authInstance = googleApi.auth2.getAuthInstance()!;
      const signedIn = authInstance.isSignedIn.get();

      let auth: GoogleAuthUser;
      if (signedIn) {
        auth = authInstance.currentUser.get();
      } else {
        auth = await authInstance.signIn({
          ux_mode: 'popup'
        });
      }

      googleUserToken = auth.getAuthResponse(true).access_token;
      setState({
        isSignedIn: true,
        isRequestPending: false,
        loggedInUserEmail: auth.getBasicProfile().getEmail()
      });
    } catch (error) {
      setState({
        isRequestPending: false,
        isSignedIn: false,
        loggedInUserEmail: null
      });

      // Reset Google auth state to allow retry
      try {
        googleApi.auth2.getAuthInstance()?.disconnect();
      } catch (disconnectError) {
        console.warn('Could not disconnect Google auth instance:', disconnectError);
      }

      // Handle all authentication errors the same way
      handleGoogleError(error, 'authentication');
    }
  }, []);

  /**
   * Simplified Google client initialization
   */
  const initGoogleClient = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        global.gapi!.load('client:auth2', {
          callback: () => {
            handleGoogleAuth().then(resolve).catch(reject);
          },
          onerror: reject,
          timeout: 10000, // Increased timeout for slower connections
          ontimeout: () => reject(new Error('Google API timeout'))
        });
      }),
    [handleGoogleAuth]
  );

  const handleSignIn = useCallback(async () => {
    setState({isRequestPending: true});

    try {
      if (!global.gapi) {
        throw new Error('Google API not loaded');
      }
      await initGoogleClient();
    } catch (error) {
      setState({isRequestPending: false});
      handleGoogleError(error, 'sign-in');
    }
  }, [initGoogleClient]);

  const handleSignOut = useCallback(() => {
    setState({isRequestPending: true});
    global
      .gapi!.auth2.getAuthInstance()!
      .signOut()
      .then(() => {
        const signedIn = !!global.gapi!.auth2.getAuthInstance()!.isSignedIn.get();
        // The class invalidated from `setState`'s completion callback, i.e. after the commit —
        // the effect below is where that now happens.
        shouldInvalidateClassroomsRef.current = true;
        setState({
          isRequestPending: false,
          isSignedIn: signedIn,
          loggedInUserEmail: null
        });
        googleUserToken = null;
      })
      .catch((error) => {
        setState({isRequestPending: false});
        handleGoogleError(error, 'sign-out');
      });
  }, []);

  const handleExportAssignment = useCallback(
    ({
      assignment,
      assignmentLink,
      googleCourseId,
      successCallback = () => {},
      errorCallback = () => {},
      classroomDueDate
    }: ExportAssignmentArgs) => {
      const assignmentState = assignment.isScheduled() ? 'DRAFT' : 'PUBLISHED';

      const scheduledTime = assignment.isScheduled()
        ? assignment.getStartDateFromSettings().utc().format()
        : null;

      const dueDate = classroomDueDate ?? assignment.getDueDateFromSettings();

      const payload: Record<string, unknown> = {
        courseId: googleCourseId,
        creationTime: assignment.getInsertedAt().utc().format(),
        description: assignment.getMessageFromSettings(),
        dueDate: {
          year: dueDate.utc().format('YYYY'),
          month: dueDate.utc().format('MM'),
          day: dueDate.utc().format('DD')
        },
        dueTime: {
          hours: dueDate.utc().format('HH'),
          minutes: dueDate.utc().format('mm')
        },
        materials: [
          {
            link: {
              url: assignmentLink,
              title: assignment.getName(),
              thumbnailUrl: assignmentLink
            }
          }
        ],
        scheduledTime: null,
        state: assignmentState,
        title: assignment.getName(),
        workType: 'ASSIGNMENT'
      };

      if (scheduledTime) {
        payload.scheduledTime = scheduledTime;
      }

      global
        .gapi!.client.classroom.courses.courseWork.create(payload)
        .then(() => {
          successCallback();
          setState({isRequestPending: false});
        })
        .catch((error) => {
          setState({isRequestPending: false});
          errorCallback(error);
          handleGoogleError(error, 'assignment-export');
        });
    },
    []
  );

  const handleImportClassroom = useCallback(
    async ({callback = () => {}, classroomId, googleClassroomId}: ImportClassroomArgs) => {
      // `.done()` is typed as the builder's loose `Record<string, unknown>`, but it produces
      // the resourcePath-bearing shape this request expects.
      const syncQuery = query()
        .mandarkEndpoint(['google_classroom_api', 'sync'])
        .customHeader({'google-classroom-api-token': googleUserToken})
        .customQuery({
          classroom_id: classroomId,
          course_id: googleClassroomId,
          issuer: masqueradeStore.getUserIdByMasqueradeState()
        })
        .done() as Parameters<typeof genericMandarkRequest>[1];

      setState({isRequestPending: true});

      try {
        const result = (await genericMandarkRequest('post', syncQuery)) as
          | ImmutableMap<string, unknown>
          | undefined;
        setState({isRequestPending: false});
        const unlinkedStudents = (
          (result?.get('unlinked_students') as List<ImmutableMap<string, string>>) ?? List()
        )
          .toArray()
          .map((student) => ({id: student.get('id')!, email: student.get('email')!}));
        callback(unlinkedStudents);
      } catch (error) {
        setState({isRequestPending: false});

        // Handle specific business errors that need special UI treatment
        const errorCode = (error as GoogleError)?.response?.body?.errors?.[0]?.code;
        if (errorCode === errorCodes.BAD_REQUEST.VALIDATION_ERROR.INVALID_EMAIL_DOMAIN) {
          setState({error: errorCode});
          return;
        }
        if (errorCode === errorCodes.FORBIDDEN.EXCEEDED_HARD_CAP_LIMIT) {
          setState({error: errorCode});
          return;
        }

        // All other errors get standard treatment
        handleGoogleError(error, 'classroom-import');
      }
    },
    []
  );

  // `componentDidUpdate`'s prevState comparison, kept explicit: the sign-out branch must not
  // run on mount, and neither branch may re-fire on an unrelated state change.
  useEffect(() => {
    const wasSignedIn = wasSignedInRef.current;
    wasSignedInRef.current = isSignedIn;

    // Handle sign-in
    if (isSignedIn && !wasSignedIn) {
      (async () => {
        try {
          // Using genericMandarkRequest here to bypass the cache (see https://github.com/albert-io/project-management/issues/2719)
          const classrooms = (await genericMandarkRequest(
            'get',
            getClassroomsQuery().done() as Parameters<typeof genericMandarkRequest>[1]
          )) as CollectionType;
          setState({classrooms});
        } catch (error) {
          handleGoogleError(error, 'classroom-fetch');
        }
      })();
    }

    // Handle sign-out
    if (wasSignedIn && !isSignedIn) {
      setState({classrooms: List()});
    }
  }, [isSignedIn]);

  useEffect(() => {
    if (!shouldInvalidateClassroomsRef.current) {
      return;
    }
    shouldInvalidateClassroomsRef.current = false;
    invalidatePartialInterest(getClassroomsQuery().done());
  });

  const value = useMemo<GoogleContextValue>(
    () => ({
      ...state,
      clearError,
      handleExportAssignment,
      handleImportClassroom,
      handleSignIn,
      handleSignOut
    }),
    [state, clearError, handleExportAssignment, handleImportClassroom, handleSignIn, handleSignOut]
  );

  return <GoogleContext.Provider value={value}>{children}</GoogleContext.Provider>;
}
